import { apiRequest } from "../utils/api"; // apiRequestのインポート
import { generateJan } from "../utils/jan"; 
import React, { useState, useEffect, useCallback, useRef } from "react";
import LabelModal from "./LabelModal";
import { useNavigate } from "react-router-dom"; // Linkをインポート
import FitText from "./FitText";
import { pdf } from '@react-pdf/renderer';
import { generateBarcodePng } from '../utils/barcodeSvgGenerator';
import { generateQRImage } from "../utils/qrImageGenerator";
import { generateQRData } from "../utils/qrUtils";
import { useAlert } from '../components/AlertModal';
import { useScrollPersistence } from "../hooks/useScrollPersistence";
import BarcodePdfDocument from './BarcodePdfDocument';
import QRPdfDocument from "../components/QRPdfDocument";
import "../styles/PriceLabels.css";


const PriceLabels = ({storeId, registerName }) => {
  const [items, setItems] = useState([]);
  const [conditionList, setConditionList] = useState([]);
  const [addoptionList, setAddoptionList] = useState([]);
  const [selectedLabelId, setSelectedLabelId] = useState(null);
  const [setCount, setSetCount] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [skuMap, setSkuMap] = useState({});
  const [otherVersionMap, setOtherVersionMap] = useState({});
  const [categoryList, setCategoryList] = useState([]);
  const [isNonStockModalOpen, setIsNonStockModalOpen] = useState(false);
  const [nonStockForm, setNonStockForm] = useState({
    category: 5,
    product_name: "グッズ",
    price: 500,
    condition: 1,
    box_damage: false,
    special_price: false,
    number: 1,
  });
  const { showAlert } = useAlert();
  const labelContainerRef = useRef(null);
  const navigate = useNavigate();// ページ移動

  // スクロール保存・復元
  const { saveScroll } = useScrollPersistence(labelContainerRef, "labeltable-container");
  useScrollPersistence(labelContainerRef, "labeltable-container");

  // API から PriceLabel のデータを取得
  const fetchPriceLabels = useCallback(async () => {
    try {
      let converted = registerName || "";
      if (converted.includes("1階")) converted = "1F";
      if (converted.includes("2階")) converted = "2F";

      const response = await apiRequest("inventory/pricelabels/", "GET");
      const filtered = response.filter(item =>
        item.label_item_id.category?.category_name?.includes(converted)
      );
      setItems(filtered);
    } catch (error) {
      console.error("PriceLabelデータの取得失敗:", error);
    }
  }, [registerName]);


  // 初回読み込み時にPriceLabelデータを取得
  useEffect(() => {
      // コンディションリストを取得
  const fetchConditions = async () => {
    try {
      const response = await apiRequest("inventory/conditions/", "GET");
      setConditionList(response); // コンディションリストをセット
    } catch (error) {
      console.error("コンディションの取得失敗:", error);
    }
  };

  // 付属品リストを取得
  const fetchAddOptions = async () => {
    try {
      const response = await apiRequest("inventory/addoptions/", "GET");
      setAddoptionList(response); // 付属品リストをセット
    } catch (error) {
      console.error("付属品の取得失敗:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiRequest("inventory/categorys/", "GET");
      setCategoryList(response);
    } catch (error) {
      console.error("カテゴリー取得失敗:", error);
    }
  };

    fetchPriceLabels();
    fetchConditions();
    fetchAddOptions();
    fetchCategories();
  },  [fetchPriceLabels]);


  


  const updateItemField = (item, field, value) => {
    if (field === "condition_id") {
      return { ...item, [field]: conditionList.find(cond => cond.id === value) || null };
    }
    if (field === "add_id") {
      return { ...item, [field]: addoptionList.find(opt => opt.id === value) || null };
    }
    return { ...item, [field]: value };
  };

  const handleChange = async (id, field, value) => {
    setItems(prevItems =>
      prevItems.map(item => {
        if (item.id !== id) return item;

        // set_plus_id の変更なら price を更新する
        if (field === "set_plus_id") {
          const storeInv = item.label_item_id.store_inventory_set?.find(inv => inv.store === storeId);
          const price = value ? storeInv?.sell_set_price : storeInv?.sell_price;

          return {
            ...item,
            set_plus_id: value,
            price: price ?? 0,
          };
        }

        return updateItemField(item, field, value);
      })
    );

    // APIに送信
    try {
      await apiRequest(`inventory/pricelabels_update/`, "POST", {
        id,
        updates: { [field]: value }
      });

      // set_plus_id変更時のみ price も送る
      if (field === "set_plus_id") {
        const label = items.find(item => item.id === id);
        const storeInv = label?.label_item_id.store_inventory_set?.find(inv => inv.store === storeId);
        const price = value ? storeInv?.sell_set_price : storeInv?.sell_price;

        await apiRequest(`inventory/pricelabels_update/`, "POST", {
          id,
          updates: { price: price ?? 0 }
        });
      }
    } catch (error) {
      console.error("商品更新失敗:", error);
    }
  };
   
  // 保留にチェックがないものだけをフィルタリング(JAN)
  const filteredItems = items.filter(item => !item.hold_flag); 
  
  // 保留にチェックがないものだけをフィルタリング(QR)
  const filteredItemsqr = items.filter(item => !item.hold_flag && item.price > 0); 

  useEffect(() => {
    const checkOtherVersions = async () => {
      const result = {};

      for (const label of items) {

        const stock =
          label.label_item_id.store_inventory_set?.find(
            inv => inv.store === storeId
          )?.stock ?? 0;

        if (stock > 0) continue;

        try {
          const res = await apiRequest(
            `inventory/related_versions/${label.label_item_id.id}/`,
            "GET"
          );

          result[label.id] = res.some(item => {
            const otherStock =
              item.store_inventory_set?.find(
                inv => inv.store === storeId
              )?.stock ?? 0;

            return otherStock > 0;
          });

        } catch (e) {
          console.error("別Ver確認失敗:", e);
        }
      }

      setOtherVersionMap(result);
    };

    if (items.length > 0 && storeId) {
      checkOtherVersions();
    }
  }, [items, storeId]);

  // 削除関係
  const handleDelete = async (id) => {
  
    try {
      const response = await apiRequest(`inventory/pricelabel/delete/${id}/`, "DELETE");
          // レスポンスをログに表示して確認

  
      if (response?.message === "削除成功") {
        setItems((prevItems) => prevItems.filter((item) => item.id !== id));
        

        // 削除後にデータを再取得
        fetchPriceLabels();
      } else {
        console.error("削除失敗:", response.status);
      }
    } catch (error) {
      console.error("削除エラー:", error);
    }
  };
  // SKU取得して画像を表示
  const fetchSkusByIds = async (idsString) => {
    if (!idsString) return [];
    try {
      const response = await apiRequest(`inventory/skus/?ids=${idsString}`, "GET");
      return response; // [{ id: 123, sku: "xxx" }, ...]
    } catch (error) {
      console.error("SKU取得失敗:", error);
      return [];
    }
  };
  const loadSkusForLabel = useCallback(async (label) => {
    if (!label.set_plus_id || skuMap[label.id]) return; // 既に取得済みならスキップ

    const result = await fetchSkusByIds(label.set_plus_id);
    setSkuMap((prev) => ({ ...prev, [label.id]: result }));
  }, [skuMap]); // skuMapが変更されるたびに再生成
  useEffect(() => {
    items.forEach(label => {
      if (label.set_plus_id) {
        loadSkusForLabel(label);  // ID文字列を渡す
      }
    });
  }, [items, loadSkusForLabel]);


  const openModal = (labelId, setCountValue) => {
    setSelectedLabelId(labelId);
    setSetCount(setCountValue);
    setIsModalOpen(true);
  };
  
  const handgenerateJan = async (id,jan) => {
      if (jan===""  ) {
        handleChange(id, "jan", generateJan(id))
        const updates = { "jan": generateJan(id) };
        handleInventoryUpdate(id, updates);
        fetchPriceLabels()
      }else{
        showAlert("JANが既にあります。削除してから押してください。");
      }
  };
  // 商品マスター更新
  const handleInventoryUpdate = async (itemId, updates) => {
    try {
      await apiRequest(`inventory/inventory/${itemId}/`, 'PATCH', updates);
    } catch (error) {
      console.error("Inventory更新失敗:", error);
    }
  };
   // 価格更新
  const updatePricesBeforeNavigate = async () => {
    for (const label of filteredItemsqr) {
      // スキップ条件: 箱傷み or 保留
      if (label.box_condition || label.hold_flag) {
        console.log(`スキップ: 箱傷み or 保留 (ID: ${label.id})`);
        continue;
      }

      const storeInventory = label.label_item_id.store_inventory_set?.find(
        inv => inv.store === storeId
      );

      if (!storeInventory) {
        console.warn(`storeInventoryが見つかりません (ID: ${label.id})`);
        continue;
      }

      const currentPrice = label.price;
      if (currentPrice == null) {
        console.warn(`priceが未設定 (ID: ${label.id})`);
        continue;
      }

      const isSet = !!label.set_plus_id;
      const storedPrice = isSet ? storeInventory.sell_set_price : storeInventory.sell_price;

      if (Number(storedPrice) !== Number(currentPrice)) {
        const updates = isSet
          ? { sell_set_price: currentPrice }
          : { sell_price: currentPrice };

        try {
          await apiRequest(`inventory/storeinventory/${storeInventory.id}/`, "PATCH", updates);
          console.log(`更新: ID=${label.id}, ${isSet ? "sell_set_price" : "sell_price"} を ${currentPrice} に`);
        } catch (error) {
          console.error(`更新失敗: ID=${label.id}`, error);
        }
      } else {
        console.log(`価格に変更なし（更新不要）: ID=${label.id}`);
      }
    }
  };
 

  // 共通関数：enrichedItemsを生成
  const generateEnrichedItems = async (items) => {
    const enrichedItems = await Promise.all(
      items.map(async (item) => {
        const barcodeImage = await generateBarcodePng(item.label_item_id.jan || "0000000000000");
        return {
          ...item,
          barcodeImage,
        };
      })
    );
    return enrichedItems;
  };

  // 印刷用
  const handleGenerateAndPrint = async () => {
    const janMissingItem = filteredItems.find(item => !item.label_item_id.jan || item.label_item_id.jan.trim() === "");

    if (janMissingItem) {
      showAlert(`ID=${janMissingItem.label_item_id.id} の商品にJANコードがありません。印刷できません。`);
      return;
    }

    const enrichedItems = await generateEnrichedItems(filteredItems);
    const instance = pdf(<BarcodePdfDocument barcodeItems={enrichedItems} />);
    const blob = await instance.toBlob();
    const arrayBuffer = await blob.arrayBuffer();

    if (window.electronAPI?.sendPdfBuffer) {
      window.electronAPI.sendPdfBuffer(arrayBuffer);
      console.log("[React] ElectronにPDFデータ送信完了");
    } else {
      console.warn("[React] electronAPI.sendPdfBufferが未定義です");
    }
  };

  const handleGenerateAndPrintQr = async () => {
    // 条件を満たすデータのみフィルタリング（価格 > 0 && 在庫 >= 1）
    const validItems = filteredItems.filter(item => {
      const stock = item.label_item_id.store_inventory_set?.find(inv => inv.store === storeId)?.stock ?? 0;
      return item.price > 0 && stock >= 1;
    });
  
    if (validItems.length === 0) {
      showAlert("印刷対象となる商品がありません（価格が0または在庫が不足）");
      return;
    }
  
    // QR付きのデータを作成
    const enrichedItems = await Promise.all(
      validItems.map(async (item) => {
        const qrData = generateQRData(item); // QRデータ文字列
        const qrImage = await generateQRImage(qrData); // QRイメージ作成
        return {
          ...item,
          qrImage,
        };
      })
    );
  
    const instance = pdf(<QRPdfDocument barcodeItems={enrichedItems} />);
    const blob = await instance.toBlob();
    const arrayBuffer = await blob.arrayBuffer();
  
    if (window.electronAPI?.sendQrPdfBuffer) {
      window.electronAPI.sendQrPdfBuffer(arrayBuffer);
      console.log("[React] QR用PDFデータをElectronに送信しました！");
  
      // 価格の更新
      await updatePricesBeforeNavigate();
  
      // 対象アイテムのみ削除
      for (const item of validItems) {
        try {
          await apiRequest(`inventory/pricelabel/delete/${item.id}/`, "DELETE");
          console.log(`PriceLabel ID=${item.id} 削除完了`);
        } catch (error) {
          console.error(`PriceLabel ID=${item.id} 削除失敗`, error);
        }
      }
  
      // 全部消したあとにリスト更新
      fetchPriceLabels();
  
    } else {
      console.warn("[React] electronAPI.sendQrPdfBuffer が未定義です");
    }
  };

  // 単体印刷 (バーコード)
  const handlePrintSingleBarcode = async (item) => {
    const jan = item.label_item_id.jan;

    if (!jan || jan.trim() === "") {
      showAlert(`ID=${item.label_item_id.id} の商品にJANコードがありません。印刷できません。`);
      return;
    }

    const barcodeImage = await generateBarcodePng(jan);
    const enrichedItem = { ...item, barcodeImage };

    const instance = pdf(<BarcodePdfDocument barcodeItems={[enrichedItem]} />);
    const blob = await instance.toBlob();
    const arrayBuffer = await blob.arrayBuffer();

    if (window.electronAPI?.sendPdfBuffer) {
      window.electronAPI.sendPdfBuffer(arrayBuffer);
      console.log(`[React] 単体バーコード印刷: ID=${item.id}`);
    }
  };

  // 単体印刷 (QR)
  const handlePrintSingleQr = async (item) => {
    const qrData = generateQRData(item);
    const qrImage = await generateQRImage(qrData);

    const enrichedItem = { ...item, qrImage };

    const printQr = async (barcodeItems, isLarge = false) => {
      const instance = pdf(
        <QRPdfDocument
          barcodeItems={barcodeItems}
          largeQrOnly={isLarge}
        />
      );

      const blob = await instance.toBlob();
      const arrayBuffer = await blob.arrayBuffer();

      if (window.electronAPI?.sendQrPdfBuffer) {
        window.electronAPI.sendQrPdfBuffer(arrayBuffer);
      } else {
        console.warn("[React] electronAPI.sendQrPdfBuffer が未定義です");
      }
    };

    try {
      // 1枚目：通常QR
      await printQr([enrichedItem], false);
      console.log(`[React] 単体QR印刷: ID=${item.id}`);

      // セット数7以上なら2枚目：拡大QRのみ
      if (Number(item.set_number || 0) >= 7) {
        await printQr([enrichedItem], true);
        console.log(`[React] 拡大QR追加印刷: ID=${item.id}`);
      }

      // 価格の更新
      await updatePricesBeforeNavigate();

      // 印刷後に削除
      await apiRequest(`inventory/pricelabel/delete/${item.id}/`, "DELETE");
      setItems(prev => prev.filter(i => i.id !== item.id));
      console.log(`ID=${item.id} を削除しました`);

    } catch (err) {
      console.error(`QR印刷または削除失敗 ID=${item.id}:`, err);
      showAlert(`QR印刷または削除に失敗しました（ID=${item.id}）`);
    }
  };

  const handleDetail = (label) => {
    saveScroll(); 
    navigate(`/inventory_detail/${label.label_item_id.id}/${storeId}`);
  };
  const handleDeleteSetItem = async (label, skuItemId) => {
    if (!label.set_plus_id) return;

    const idList = label.set_plus_id
      .split(',')
      .map(id => id.trim())
      .filter(id => id && id !== String(skuItemId));

    const newSetPlusId = idList.join(',');

    try {
      await apiRequest(`inventory/pricelabels_update/`, "POST", {
        id: label.id,
        updates: { set_plus_id: newSetPlusId }
      });

      setItems(prevItems =>
        prevItems.map(item =>
          item.id === label.id ? { ...item, set_plus_id: newSetPlusId } : item
        )
      );

      setSkuMap(prev => {
        const updated = { ...prev };
        updated[label.id] = updated[label.id]?.filter(obj => obj.id !== skuItemId);
        return updated;
      });

    } catch (error) {
      console.error("set_plus_id 更新失敗:", error);
      showAlert("セット商品の削除に失敗しました");
    }
  };
  
  // ラベル追加
  const handleAddToLabel = async (id, price) => {
      try {
        const response = await apiRequest(`inventory/add_to_label/`, "POST", {
          id,
          price,
        });
  
        if (response.message) {
          showAlert(response.message);
        }
        // リスト更新
        fetchPriceLabels();
        
      } catch (error) {
        console.error("Error:", error);
        showAlert("エラーが発生しました");
      }
    };

  // ノンストックQR
  const handleGenerateAndPrintNonStockQr = async () => {
    const price = Number(nonStockForm.price);

    if (!nonStockForm.product_name) {
      showAlert("商品名を入力してください");
      return;
    }

    if (!price || price <= 0) {
      showAlert("価格を入力してください");
      return;
    }

    const qrData = [
      "NONSTOCK",
      nonStockForm.category,
      nonStockForm.product_name,
      price,
      nonStockForm.condition,
      nonStockForm.box_damage ? 1 : 0,
      nonStockForm.special_price ? 1 : 0,
    ].join(",");

    const qrImage = await generateQRImage(qrData);

    const nonStockItem = {
      id: Date.now(),
      number: Number(nonStockForm.number || 1),
      qrImage,

      // QRPdfDocument側で使いそうな値
      price,
      condition_id: conditionList.find(c => c.id === Number(nonStockForm.condition)) || null,
      box_condition: nonStockForm.box_damage ? 1 : 0,
      price_sp: nonStockForm.special_price ? 1 : 0,

      label_item_id: {
        id: null,
        item_name: nonStockForm.product_name,
        jan: "",
        category: {
          id: Number(nonStockForm.category),
        },
        store_inventory_set: [],
      },
    };

    const instance = pdf(<QRPdfDocument barcodeItems={[nonStockItem]} />);
    const blob = await instance.toBlob();
    const arrayBuffer = await blob.arrayBuffer();

    if (window.electronAPI?.sendQrPdfBuffer) {
      window.electronAPI.sendQrPdfBuffer(arrayBuffer);
      setIsNonStockModalOpen(false);
    } else {
      showAlert("QR印刷APIが未定義です");
    }
  };  
  return (
    <div className="label-container"> 
      <div className="barcode-button-area"> 
        <button className="print-qrcode" onClick={handleGenerateAndPrintQr}>
          値札印刷
        </button>
        <button className="print-barcode" onClick={handleGenerateAndPrint}>
          バーコード印刷
        </button>
        <button
          className="print-nonstock"
          onClick={() => setIsNonStockModalOpen(true)}
        >
          NonStockQR
        </button>
      </div>
    <div ref={labelContainerRef} className="labeltable-container"> 
      {items.length > 0 ? (
        items.map((label) => {

          const hasOtherVersion = !!otherVersionMap[label.id];

          return (
            <div
              className={`labeltable-box ${
                hasOtherVersion ? "labeltable-box-other-version" : ""
              }`}
              key={label.id}
            >
            <div className="labeltable-button-box">
              <div className="labeltable-amazon">
                <a href={`https://www.amazon.co.jp/s?k=${label.label_item_id.jan}`} target="_blank" rel="noopener noreferrer">
                <button>WEB</button>
                </a>
              </div>
               <div className="labeltable-price-print">
                <button onClick={() => handlePrintSingleQr(label)}>QR</button>
              </div>
              <div className="labeltable-jan-print">
                <button onClick={() => handlePrintSingleBarcode(label)}>JAN</button>
              </div>
              <div className="labeltable-label-print">
                <button onClick={() => handleAddToLabel(label.label_item_id.id,(label.label_item_id.store_inventory_set?.find(inv => inv.store === storeId)?.sell_price ?? 0))}>追加</button>
              </div>
            </div>   
            <div className="labeltable-labeltable-row">
              <div className="label_h_price">{label.set_plus_id ? "SET価格:￥" : "価格:￥"}</div>
              <div className="label_h_jan" onClick={() => handgenerateJan(label.label_item_id.id,label.label_item_id.jan?? "")}><span>JAN</span>：</div>
              <div className="label_h_stock">在庫：</div>
              <div className="label_h_set">セット数</div>
              <div className="label_h_number">印刷枚数：</div>
              <div className="label-price-line">
                <div className="set_h_inprice">価格:￥</div>
                <div className="set_h_insetprice">SET:￥</div>
              </div>
              <div className="label_h_comment1">コメント1：</div>
              <div className="label_h_comment2">コメント2：</div>
              <div className="labelflag">
                <label className="label-toggle">
                <input
                  type="checkbox"
                  checked={label.hold_flag}
                  onChange={(e) => handleChange(label.id, "hold_flag", e.target.checked)}
                />
                <span className="label-toggle-slider"></span>
                <span className="label-toggle-label">保留</span>
              </label>
              </div>
              <div className="labelid"><button className="inventory_table_item_id" onClick={() => handleDetail(label)}>📝<br/>{label.label_item_id.id}</button></div>
              <div className="labelname">
                <FitText maxFontSize={16} minFontSize={8}>
                  {label.label_item_id.item_name}
                </FitText>
              </div>
              <div className="labelpic"><img src={`/media/product_images/${label.label_item_id.sku}.jpg`} alt="商品画像"/></div>
              <div className="labeljan">{label.label_item_id.jan}</div>
              <div className="labelprice">
                <input
                  type="number"
                  value={
                    label.price

                  }
                  onChange={(e) => handleChange(label.id, "price", Number(e.target.value))}
                  onWheel={(e) => e.target.blur()}
                />
              </div>
              <div className="labelcondition">
                <select
                  value={label.condition_id?.id || ""}  // 
                  onChange={(e) => handleChange(label.id, "condition_id", Number(e.target.value) || null)}
                >
                  {conditionList.map((condition) => (
                    <option key={condition.id} value={condition.id}>
                      {condition.condition_jpn}
                    </option>
                  ))}
                </select>
              </div>
                <div className="labelpricesp">
                  <input
                    type="checkbox"
                    id={`price_sp_${label.id}`}
                    checked={label.price_sp}
                    onChange={(e) => handleChange(label.id, "price_sp", e.target.checked)}
                  />
                  <label htmlFor={`price_sp_${label.id}`} className="glow-button">
                    特別価格
                  </label>
                </div>
            <div className="labelboxcondition">
                <input
                  type="checkbox"
                  id={`box_condition_${label.id}`}
                  checked={label.box_condition}
                  onChange={(e) => handleChange(label.id, "box_condition", e.target.checked)}
                />
                <label htmlFor={`box_condition_${label.id}`} className="glow-button-yellow">
                  箱傷み
                </label>
              </div>
              <div className="labeladdop">
                <select
                  value={label.add_id ? label.add_id.id : "none"}
                  onChange={(e) =>
                    handleChange(label.id, "add_id", e.target.value === "none" ? null : Number(e.target.value))
                  }
                >
                  <option value="none">未選択</option>
                  {addoptionList.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.add_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="labelset">
                <input
                  type="number"
                  value={label.set_number || ''}
                  onChange={(e) => handleChange(label.id, "set_number", Number(e.target.value))}
                />
                </div>
              <div className="labelsetinput">
                <button onClick={() => openModal(label.id, label.set_number)}>入力</button>
              </div>
              <div className="labelnumber">
                <input
                  type="number"
                  value={label.number || ''}
                  onChange={(e) => handleChange(label.id, "number", Number(e.target.value))}
                />
              </div>
              <div className="label-stock">{(label.label_item_id.store_inventory_set?.find(inv => inv.store === storeId)?.stock ?? 0)}</div>
              <div className="label-inprice-line">
                <div className="label-inprice">{(label.label_item_id.store_inventory_set?.find(inv => inv.store === storeId)?.sell_price ?? 0)}</div>
                <div className="label-insetprice">{(label.label_item_id.store_inventory_set?.find(inv => inv.store === storeId)?.sell_set_price ?? 0)}</div>
              </div>
              <div className="label-skuspic">
                {skuMap[label.id]?.map((skuObj) => (
                  <div key={skuObj.id} className="label-skuspic-wrapper">
                    <img
                      src={`/media/product_images/${skuObj.sku}.jpg`}
                      alt="セット商品画像"
                      onClick={() => navigate(`/Inventory_detail/${skuObj.id}/${storeId}`)}
                    />
                    <button
                      className="delete-sku-button"
                      onClick={() => handleDeleteSetItem(label, skuObj.id)}
                    >
                      ✖
                    </button>
                  </div>
                ))}
              </div>
              <div className="labelcomment1">
                <input
                  type="text"
                  value={label.comment1 || ''}
                  onChange={(e) => handleChange(label.id, "comment1", e.target.value)}
                />
              </div>
              <div className="labelcomment2">
                <input
                  type="text"
                  value={label.comment2 || ''}
                  onChange={(e) => handleChange(label.id, "comment2", e.target.value)}
                />
              </div>
              <div className="labeldelete">
                <button onClick={() => handleDelete(label.id)}>✖</button>
              </div>
            </div>
          </div>
         
        )})
          ) : (
            <div className="labeltable labeltable-row">
              <div style={{ gridColumn: "1 / -1" }}>結果がありません。</div>
            </div>
          )}
      </div>

      <LabelModal 
      isOpen={isModalOpen} 
      onClose={() => {setIsModalOpen(false);fetchPriceLabels();  // モーダルを閉じた後にデータを再取得
              }} 
      labelId={selectedLabelId} 
      setCount={setCount} 
      setIsModalOpen={setIsModalOpen}
      />
      {isNonStockModalOpen && (
        <div className="nonstock-modal-backdrop">
          <div className="nonstock-modal">
            <h3>NONSTOCK QR印刷</h3>

            <label>
              商品名
              <input
                type="text"
                value={nonStockForm.product_name}
                onChange={(e) =>
                  setNonStockForm(prev => ({ ...prev, product_name: e.target.value }))
                }
              />
            </label>

            <label>
              カテゴリー
              <select
                value={nonStockForm.category}
                onChange={(e) =>
                  setNonStockForm(prev => ({
                    ...prev,
                    category: Number(e.target.value),
                  }))
                }
              >
                {categoryList.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.category_name}
                  </option>
                ))}
              </select>
            </label>

            <label>
              価格
              <input
                type="number"
                value={nonStockForm.price}
                onChange={(e) =>
                  setNonStockForm(prev => ({ ...prev, price: Number(e.target.value) }))
                }
              />
            </label>

            <label>
              状態
              <select
                value={nonStockForm.condition}
                onChange={(e) =>
                  setNonStockForm(prev => ({ ...prev, condition: Number(e.target.value) }))
                }
              >
                {conditionList.map((condition) => (
                  <option key={condition.id} value={condition.id}>
                    {condition.condition_jpn}
                  </option>
                ))}
              </select>
            </label>

            <label className="nonstock-modal-checkbox">
              <input
                type="checkbox"
                checked={nonStockForm.box_damage}
                onChange={(e) =>
                  setNonStockForm(prev => ({
                    ...prev,
                    box_damage: e.target.checked
                  }))
                }
              />
              箱傷み
            </label>

            <label className="nonstock-modal-checkbox">
              <input
                type="checkbox"
                checked={nonStockForm.special_price}
                onChange={(e) =>
                  setNonStockForm(prev => ({
                    ...prev,
                    special_price: e.target.checked
                  }))
                }
              />
              特価
            </label>
            <label>
              印刷枚数
              <input
                type="number"
                min="1"
                value={nonStockForm.number}
                onChange={(e) =>
                  setNonStockForm(prev => ({
                    ...prev,
                    number: Number(e.target.value),
                  }))
                }
              />
            </label>

            <div className="nonstock-modal-buttons">
              <button onClick={handleGenerateAndPrintNonStockQr}>
                印刷
              </button>

              <button onClick={() => setIsNonStockModalOpen(false)}>
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceLabels;

// 右クリック用の関数をエクスポート。他の場所で使います
export const printLabelAndDeleteById = async (labelItem, price, showAlert) => {
  try {
    if (!labelItem?.jan || labelItem.jan.trim() === "") {
      showAlert(`ID=${labelItem.id} にJANコードがありません`);
      return;
    }

    const barcodeImage = await generateBarcodePng(labelItem.jan);
    const enrichedItem = {
      label_item_id: labelItem,
      label_price: price,
      barcodeImage,
    };

    const instance = pdf(<BarcodePdfDocument barcodeItems={[enrichedItem]} />);
    const blob = await instance.toBlob();
    const arrayBuffer = await blob.arrayBuffer();

    if (window.electronAPI?.sendPdfBuffer) {
      window.electronAPI.sendPdfBuffer(arrayBuffer);
      console.log(`[右クリック] 印刷完了: ID=${labelItem.id}`);
    }
  } catch (err) {
    console.error("印刷または削除に失敗:", err);
    showAlert("印刷処理中にエラーが発生しました");
  }
};

export const addLabelAndPrintQr = async (inventoryId, storeId, showAlert) => {
  try {
    // Inventory取得（価格取得のため）
    const item = await apiRequest(`inventory/inventory/${inventoryId}/`, "GET");
    const price = item.store_inventory_set?.find(inv => inv.store === storeId)?.sell_price ?? 0;

    // PriceLabel に一時追加
    const addedLabel = await apiRequest(`inventory/add_to_label/`, "POST", {
      id: inventoryId,
      price: price,
    });
    console.log(addedLabel)

    const labelId = addedLabel.id;

    // PriceLabel 詳細取得（QR印刷に必要な構造）
    const label = await apiRequest(`inventory/pricelabels/${labelId}/`, "GET");

    // QRデータ生成
    const qrData = generateQRData(label);
    const qrImage = await generateQRImage(qrData);
    const enrichedItem = { ...label, qrImage };

    // PDF生成
    const instance = pdf(<QRPdfDocument barcodeItems={[enrichedItem]} />);
    const blob = await instance.toBlob();
    const arrayBuffer = await blob.arrayBuffer();

    if (window.electronAPI?.sendQrPdfBuffer) {
      window.electronAPI.sendQrPdfBuffer(arrayBuffer);
      console.log(`[QR印刷] 完了: ID=${labelId}`);
    } else {
      console.warn("❌ Electron QR印刷API未定義");
    }

    // 後始末：ラベル削除
    await apiRequest(`inventory/pricelabel/delete/${labelId}/`, "DELETE");
    console.log(`[QR印刷] 削除完了: ID=${labelId}`);

  } catch (error) {
    console.error("❌ QR印刷中のエラー:", error);
    showAlert("QR印刷に失敗しました");
  }
};

export const addLabelAndPrintQr_special = async (inventoryId, storeId, showAlert) => {
  try {
    // Inventory取得（価格取得のため）
    const item = await apiRequest(`inventory/inventory/${inventoryId}/`, "GET");
    const price = item.store_inventory_set?.find(inv => inv.store === storeId)?.sell_price ?? 0;

    // PriceLabel に一時追加
    const addedLabel = await apiRequest(`inventory/add_to_label/`, "POST", {
      id: inventoryId,
      price: price,
      price_sp: 1,
    });
    console.log(addedLabel)

    const labelId = addedLabel.id;

    // PriceLabel 詳細取得（QR印刷に必要な構造）
    const label = await apiRequest(`inventory/pricelabels/${labelId}/`, "GET");

    // QRデータ生成
    const qrData = generateQRData(label);
    const qrImage = await generateQRImage(qrData);
    const enrichedItem = { ...label, qrImage };

    // PDF生成
    const instance = pdf(<QRPdfDocument barcodeItems={[enrichedItem]} />);
    const blob = await instance.toBlob();
    const arrayBuffer = await blob.arrayBuffer();

    if (window.electronAPI?.sendQrPdfBuffer) {
      window.electronAPI.sendQrPdfBuffer(arrayBuffer);
      console.log(`[QR印刷] 完了: ID=${labelId}`);
    } else {
      console.warn("❌ Electron QR印刷API未定義");
    }

    // 後始末：ラベル削除
    await apiRequest(`inventory/pricelabel/delete/${labelId}/`, "DELETE");
    console.log(`[QR印刷] 削除完了: ID=${labelId}`);

  } catch (error) {
    console.error("❌ QR印刷中のエラー:", error);
    showAlert("QR印刷に失敗しました");
  }
};